/**
 * Centralized Theme Configuration
 * 
 * This file contains all color schemes and theme configurations.
 * Change the ACTIVE_THEME to switch between different color schemes instantly.
 */

export interface ColorPalette {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  950: string;
}

export interface ThemeConfig {
  name: string;
  description: string;
  primary: ColorPalette;
  accent: ColorPalette;
  neutral: ColorPalette;
  success: Partial<ColorPalette>;
  warning: Partial<ColorPalette>;
  danger: Partial<ColorPalette>;
  // CSS custom properties for dynamic colors
  cssVars: {
    focusColor: string;
    glowColor: string;
    textGradientFrom: string;
    textGradientTo: string;
  };
}

// 🎨 Available Theme Configurations

export const GOLDEN_CELEBRATION: ThemeConfig = {
  name: 'Golden Celebration',
  description: 'Warm golden/orange theme matching the celebratory banner',
  primary: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
    950: '#020617',
  },
  accent: {
    50: '#fffdf0',
    100: '#fefce8',
    200: '#fef3c7',
    300: '#fde68a',
    400: '#fcd34d',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
    950: '#451a03',
  },
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#e5e5e5',
    300: '#d4d4d4',
    400: '#a3a3a3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#262626',
    900: '#171717',
    950: '#0a0a0a',
  },
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
  },
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    500: '#f59e0b',
    600: '#d97706',
  },
  danger: {
    50: '#fef2f2',
    100: '#fee2e2',
    500: '#ef4444',
    600: '#dc2626',
  },
  cssVars: {
    focusColor: '#f59e0b',
    glowColor: 'rgb(245 158 11 / 0.15)',
    textGradientFrom: '#d97706',
    textGradientTo: '#f59e0b',
  }
};

export const CLASSIC_RED: ThemeConfig = {
  name: 'Classic Red',
  description: 'Traditional red theme for classic fireworks branding',
  primary: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
    950: '#020617',
  },
  accent: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
    950: '#450a0a',
  },
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#e5e5e5',
    300: '#d4d4d4',
    400: '#a3a3a3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#262626',
    900: '#171717',
    950: '#0a0a0a',
  },
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
  },
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    500: '#f59e0b',
    600: '#d97706',
  },
  danger: {
    50: '#fef2f2',
    100: '#fee2e2',
    500: '#ef4444',
    600: '#dc2626',
  },
  cssVars: {
    focusColor: '#dc2626',
    glowColor: 'rgb(239 68 68 / 0.15)',
    textGradientFrom: '#dc2626',
    textGradientTo: '#ef4444',
  }
};

export const EMERALD_LUXURY: ThemeConfig = {
  name: 'Emerald Luxury',
  description: 'Sophisticated emerald green for premium branding',
  primary: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
    950: '#020617',
  },
  accent: {
    50: '#ecfdf5',
    100: '#d1fae5',
    200: '#a7f3d0',
    300: '#6ee7b7',
    400: '#34d399',
    500: '#10b981',
    600: '#059669',
    700: '#047857',
    800: '#065f46',
    900: '#064e3b',
    950: '#022c22',
  },
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#e5e5e5',
    300: '#d4d4d4',
    400: '#a3a3a3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#262626',
    900: '#171717',
    950: '#0a0a0a',
  },
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
  },
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    500: '#f59e0b',
    600: '#d97706',
  },
  danger: {
    50: '#fef2f2',
    100: '#fee2e2',
    500: '#ef4444',
    600: '#dc2626',
  },
  cssVars: {
    focusColor: '#059669',
    glowColor: 'rgb(16 185 129 / 0.15)',
    textGradientFrom: '#059669',
    textGradientTo: '#10b981',
  }
};

export const ROYAL_PURPLE: ThemeConfig = {
  name: 'Royal Purple',
  description: 'Majestic purple theme for premium fireworks experience',
  primary: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
    950: '#020617',
  },
  accent: {
    50: '#faf5ff',
    100: '#f3e8ff',
    200: '#e9d5ff',
    300: '#d8b4fe',
    400: '#c084fc',
    500: '#a855f7',
    600: '#9333ea',
    700: '#7c3aed',
    800: '#6b21a8',
    900: '#581c87',
    950: '#3b0764',
  },
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#e5e5e5',
    300: '#d4d4d4',
    400: '#a3a3a3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#262626',
    900: '#171717',
    950: '#0a0a0a',
  },
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
  },
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    500: '#f59e0b',
    600: '#d97706',
  },
  danger: {
    50: '#fef2f2',
    100: '#fee2e2',
    500: '#ef4444',
    600: '#dc2626',
  },
  cssVars: {
    focusColor: '#9333ea',
    glowColor: 'rgb(168 85 247 / 0.15)',
    textGradientFrom: '#9333ea',
    textGradientTo: '#a855f7',
  }
};

export const VIBRANT_CELEBRATION: ThemeConfig = {
  name: 'Vibrant Celebration',
  description: 'Energetic pink and purple theme inspired by the festive banner',
  primary: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
    950: '#020617',
  },
  accent: {
    50: '#fef1f7',
    100: '#fee7f0',
    200: '#fecdd3',
    300: '#fda4af',
    400: '#fb7185',
    500: '#f43f5e',
    600: '#e11d48',
    700: '#be185d',
    800: '#9f1239',
    900: '#881337',
    950: '#4c0519',
  },
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#e5e5e5',
    300: '#d4d4d4',
    400: '#a3a3a3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#262626',
    900: '#171717',
    950: '#0a0a0a',
  },
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
  },
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    500: '#f59e0b',
    600: '#d97706',
  },
  danger: {
    50: '#fef2f2',
    100: '#fee2e2',
    500: '#ef4444',
    600: '#dc2626',
  },
  cssVars: {
    focusColor: '#e11d48',
    glowColor: 'rgb(244 63 94 / 0.15)',
    textGradientFrom: '#e11d48',
    textGradientTo: '#f43f5e',
  }
};

// 🎯 ACTIVE THEME SELECTION
// Change this to switch themes instantly across the entire application
export const ACTIVE_THEME: ThemeConfig = VIBRANT_CELEBRATION;

// 🛠️ Theme Utilities

/**
 * Get all available themes
 */
export const getAvailableThemes = (): ThemeConfig[] => [
  GOLDEN_CELEBRATION,
  CLASSIC_RED,
  EMERALD_LUXURY,
  ROYAL_PURPLE,
  VIBRANT_CELEBRATION,
];

/**
 * Get theme by name
 */
export const getThemeByName = (name: string): ThemeConfig | undefined => {
  return getAvailableThemes().find(theme => theme.name === name);
};

/**
 * Generate CSS that overrides Tailwind classes with theme colors
 */
export const generateThemeCSS = (theme: ThemeConfig = ACTIVE_THEME): string => {
  return `
    :root {
      --theme-focus-color: ${theme.cssVars.focusColor};
      --theme-glow-color: ${theme.cssVars.glowColor};
      --theme-text-gradient-from: ${theme.cssVars.textGradientFrom};
      --theme-text-gradient-to: ${theme.cssVars.textGradientTo};
    }
    
    /* Override hardcoded gray colors with neutral theme colors */
    .bg-gray-50 { background-color: ${theme.neutral[50]} !important; }
    .bg-gray-100 { background-color: ${theme.neutral[100]} !important; }
    .bg-gray-200 { background-color: ${theme.neutral[200]} !important; }
    .bg-gray-300 { background-color: ${theme.neutral[300]} !important; }
    .bg-gray-400 { background-color: ${theme.neutral[400]} !important; }
    .bg-gray-500 { background-color: ${theme.neutral[500]} !important; }
    .bg-gray-600 { background-color: ${theme.neutral[600]} !important; }
    .bg-gray-700 { background-color: ${theme.neutral[700]} !important; }
    .bg-gray-800 { background-color: ${theme.neutral[800]} !important; }
    .bg-gray-900 { background-color: ${theme.neutral[900]} !important; }
    
    .text-gray-50 { color: ${theme.neutral[50]} !important; }
    .text-gray-100 { color: ${theme.neutral[100]} !important; }
    .text-gray-200 { color: ${theme.neutral[200]} !important; }
    .text-gray-300 { color: ${theme.neutral[300]} !important; }
    .text-gray-400 { color: ${theme.neutral[400]} !important; }
    .text-gray-500 { color: ${theme.neutral[500]} !important; }
    .text-gray-600 { color: ${theme.neutral[600]} !important; }
    .text-gray-700 { color: ${theme.neutral[700]} !important; }
    .text-gray-800 { color: ${theme.neutral[800]} !important; }
    .text-gray-900 { color: ${theme.neutral[900]} !important; }
    
    .border-gray-50 { border-color: ${theme.neutral[50]} !important; }
    .border-gray-100 { border-color: ${theme.neutral[100]} !important; }
    .border-gray-200 { border-color: ${theme.neutral[200]} !important; }
    .border-gray-300 { border-color: ${theme.neutral[300]} !important; }
    .border-gray-400 { border-color: ${theme.neutral[400]} !important; }
    .border-gray-500 { border-color: ${theme.neutral[500]} !important; }
    .border-gray-600 { border-color: ${theme.neutral[600]} !important; }
    .border-gray-700 { border-color: ${theme.neutral[700]} !important; }
    .border-gray-800 { border-color: ${theme.neutral[800]} !important; }
    .border-gray-900 { border-color: ${theme.neutral[900]} !important; }
    
    .hover\\:bg-gray-50:hover { background-color: ${theme.neutral[50]} !important; }
    .hover\\:bg-gray-100:hover { background-color: ${theme.neutral[100]} !important; }
    .hover\\:bg-gray-200:hover { background-color: ${theme.neutral[200]} !important; }
    .hover\\:bg-gray-300:hover { background-color: ${theme.neutral[300]} !important; }
    .hover\\:text-gray-600:hover { color: ${theme.neutral[600]} !important; }
    .hover\\:text-gray-700:hover { color: ${theme.neutral[700]} !important; }
    
    /* Override semantic color backgrounds to use theme colors */
    .bg-green-50 { background-color: ${theme.success[50] || '#f0fdf4'} !important; }
    .bg-green-100 { background-color: ${theme.success[100] || '#dcfce7'} !important; }
    .bg-green-500 { background-color: ${theme.success[500] || '#22c55e'} !important; }
    .bg-green-600 { background-color: ${theme.success[600] || '#16a34a'} !important; }
    .bg-green-700 { background-color: ${theme.success[700] || '#15803d'} !important; }
    .text-green-600 { color: ${theme.success[600] || '#16a34a'} !important; }
    .text-green-700 { color: ${theme.success[700] || '#15803d'} !important; }
    .text-green-800 { color: ${theme.success[700] || '#15803d'} !important; }
    .border-green-200 { border-color: ${theme.success[100] || '#dcfce7'} !important; }
    
    .bg-yellow-50 { background-color: ${theme.warning[50] || '#fffbeb'} !important; }
    .bg-yellow-100 { background-color: ${theme.warning[100] || '#fef3c7'} !important; }
    .bg-yellow-200 { background-color: ${theme.warning[100] || '#fef3c7'} !important; }
    .bg-yellow-400 { background-color: ${theme.warning[500] || '#f59e0b'} !important; }
    .bg-yellow-500 { background-color: ${theme.warning[500] || '#f59e0b'} !important; }
    .text-yellow-700 { color: ${theme.warning[600] || '#d97706'} !important; }
    .text-yellow-800 { color: ${theme.warning[600] || '#d97706'} !important; }
    .border-yellow-200 { border-color: ${theme.warning[100] || '#fef3c7'} !important; }
    
    .bg-blue-50 { background-color: ${theme.primary[50]} !important; }
    .bg-blue-100 { background-color: ${theme.primary[100]} !important; }
    .bg-blue-200 { background-color: ${theme.primary[200]} !important; }
    .text-blue-600 { color: ${theme.primary[600]} !important; }
    .text-blue-700 { color: ${theme.primary[700]} !important; }
    .text-blue-800 { color: ${theme.primary[800]} !important; }
    .border-blue-200 { border-color: ${theme.primary[200]} !important; }
    
    .bg-purple-50 { background-color: ${theme.accent[50]} !important; }
    .bg-purple-100 { background-color: ${theme.accent[100]} !important; }
    .text-purple-600 { color: ${theme.accent[600]} !important; }
    .text-purple-700 { color: ${theme.accent[700]} !important; }
    
    /* Override Tailwind accent colors with theme colors */
    .bg-accent-50 { background-color: ${theme.accent[50]} !important; }
    .bg-accent-100 { background-color: ${theme.accent[100]} !important; }
    .bg-accent-200 { background-color: ${theme.accent[200]} !important; }
    .bg-accent-300 { background-color: ${theme.accent[300]} !important; }
    .bg-accent-400 { background-color: ${theme.accent[400]} !important; }
    .bg-accent-500 { background-color: ${theme.accent[500]} !important; }
    .bg-accent-600 { background-color: ${theme.accent[600]} !important; }
    .bg-accent-700 { background-color: ${theme.accent[700]} !important; }
    .bg-accent-800 { background-color: ${theme.accent[800]} !important; }
    .bg-accent-900 { background-color: ${theme.accent[900]} !important; }
    
    .text-accent-50 { color: ${theme.accent[50]} !important; }
    .text-accent-100 { color: ${theme.accent[100]} !important; }
    .text-accent-200 { color: ${theme.accent[200]} !important; }
    .text-accent-300 { color: ${theme.accent[300]} !important; }
    .text-accent-400 { color: ${theme.accent[400]} !important; }
    .text-accent-500 { color: ${theme.accent[500]} !important; }
    .text-accent-600 { color: ${theme.accent[600]} !important; }
    .text-accent-700 { color: ${theme.accent[700]} !important; }
    .text-accent-800 { color: ${theme.accent[800]} !important; }
    .text-accent-900 { color: ${theme.accent[900]} !important; }
    
    .border-accent-50 { border-color: ${theme.accent[50]} !important; }
    .border-accent-100 { border-color: ${theme.accent[100]} !important; }
    .border-accent-200 { border-color: ${theme.accent[200]} !important; }
    .border-accent-300 { border-color: ${theme.accent[300]} !important; }
    .border-accent-400 { border-color: ${theme.accent[400]} !important; }
    .border-accent-500 { border-color: ${theme.accent[500]} !important; }
    .border-accent-600 { border-color: ${theme.accent[600]} !important; }
    .border-accent-700 { border-color: ${theme.accent[700]} !important; }
    .border-accent-800 { border-color: ${theme.accent[800]} !important; }
    .border-accent-900 { border-color: ${theme.accent[900]} !important; }
    
    /* Hover states */
    .hover\\:bg-accent-50:hover { background-color: ${theme.accent[50]} !important; }
    .hover\\:bg-accent-100:hover { background-color: ${theme.accent[100]} !important; }
    .hover\\:bg-accent-200:hover { background-color: ${theme.accent[200]} !important; }
    .hover\\:bg-accent-300:hover { background-color: ${theme.accent[300]} !important; }
    .hover\\:bg-accent-400:hover { background-color: ${theme.accent[400]} !important; }
    .hover\\:bg-accent-500:hover { background-color: ${theme.accent[500]} !important; }
    .hover\\:bg-accent-600:hover { background-color: ${theme.accent[600]} !important; }
    .hover\\:bg-accent-700:hover { background-color: ${theme.accent[700]} !important; }
    .hover\\:bg-accent-800:hover { background-color: ${theme.accent[800]} !important; }
    .hover\\:bg-accent-900:hover { background-color: ${theme.accent[900]} !important; }
    
    .hover\\:text-accent-50:hover { color: ${theme.accent[50]} !important; }
    .hover\\:text-accent-100:hover { color: ${theme.accent[100]} !important; }
    .hover\\:text-accent-200:hover { color: ${theme.accent[200]} !important; }
    .hover\\:text-accent-300:hover { color: ${theme.accent[300]} !important; }
    .hover\\:text-accent-400:hover { color: ${theme.accent[400]} !important; }
    .hover\\:text-accent-500:hover { color: ${theme.accent[500]} !important; }
    .hover\\:text-accent-600:hover { color: ${theme.accent[600]} !important; }
    .hover\\:text-accent-700:hover { color: ${theme.accent[700]} !important; }
    .hover\\:text-accent-800:hover { color: ${theme.accent[800]} !important; }
    .hover\\:text-accent-900:hover { color: ${theme.accent[900]} !important; }
    
    .hover\\:border-accent-50:hover { border-color: ${theme.accent[50]} !important; }
    .hover\\:border-accent-100:hover { border-color: ${theme.accent[100]} !important; }
    .hover\\:border-accent-200:hover { border-color: ${theme.accent[200]} !important; }
    .hover\\:border-accent-300:hover { border-color: ${theme.accent[300]} !important; }
    .hover\\:border-accent-400:hover { border-color: ${theme.accent[400]} !important; }
    .hover\\:border-accent-500:hover { border-color: ${theme.accent[500]} !important; }
    .hover\\:border-accent-600:hover { border-color: ${theme.accent[600]} !important; }
    .hover\\:border-accent-700:hover { border-color: ${theme.accent[700]} !important; }
    .hover\\:border-accent-800:hover { border-color: ${theme.accent[800]} !important; }
    .hover\\:border-accent-900:hover { border-color: ${theme.accent[900]} !important; }
    
    /* Gradient color overrides */
    .from-accent-50 { --tw-gradient-from: ${theme.accent[50]} var(--tw-gradient-from-position) !important; }
    .from-accent-100 { --tw-gradient-from: ${theme.accent[100]} var(--tw-gradient-from-position) !important; }
    .from-accent-200 { --tw-gradient-from: ${theme.accent[200]} var(--tw-gradient-from-position) !important; }
    .from-accent-300 { --tw-gradient-from: ${theme.accent[300]} var(--tw-gradient-from-position) !important; }
    .from-accent-400 { --tw-gradient-from: ${theme.accent[400]} var(--tw-gradient-from-position) !important; }
    .from-accent-500 { --tw-gradient-from: ${theme.accent[500]} var(--tw-gradient-from-position) !important; }
    .from-accent-600 { --tw-gradient-from: ${theme.accent[600]} var(--tw-gradient-from-position) !important; }
    .from-accent-700 { --tw-gradient-from: ${theme.accent[700]} var(--tw-gradient-from-position) !important; }
    .from-accent-800 { --tw-gradient-from: ${theme.accent[800]} var(--tw-gradient-from-position) !important; }
    .from-accent-900 { --tw-gradient-from: ${theme.accent[900]} var(--tw-gradient-from-position) !important; }
    
    .to-accent-50 { --tw-gradient-to: ${theme.accent[50]} var(--tw-gradient-to-position) !important; }
    .to-accent-100 { --tw-gradient-to: ${theme.accent[100]} var(--tw-gradient-to-position) !important; }
    .to-accent-200 { --tw-gradient-to: ${theme.accent[200]} var(--tw-gradient-to-position) !important; }
    .to-accent-300 { --tw-gradient-to: ${theme.accent[300]} var(--tw-gradient-to-position) !important; }
    .to-accent-400 { --tw-gradient-to: ${theme.accent[400]} var(--tw-gradient-to-position) !important; }
    .to-accent-500 { --tw-gradient-to: ${theme.accent[500]} var(--tw-gradient-to-position) !important; }
    .to-accent-600 { --tw-gradient-to: ${theme.accent[600]} var(--tw-gradient-to-position) !important; }
    .to-accent-700 { --tw-gradient-to: ${theme.accent[700]} var(--tw-gradient-to-position) !important; }
    .to-accent-800 { --tw-gradient-to: ${theme.accent[800]} var(--tw-gradient-to-position) !important; }
    .to-accent-900 { --tw-gradient-to: ${theme.accent[900]} var(--tw-gradient-to-position) !important; }
    
    /* Focus styles update */
    button:focus-visible,
    input:focus-visible,
    select:focus-visible,
    textarea:focus-visible,
    a:focus-visible {
      outline: 2px solid ${theme.cssVars.focusColor} !important;
      outline-offset: 2px;
    }
    
    /* Text gradient utility update */
    .text-gradient-accent {
      background: linear-gradient(135deg, ${theme.cssVars.textGradientFrom}, ${theme.cssVars.textGradientTo}) !important;
      -webkit-background-clip: text !important;
      -webkit-text-fill-color: transparent !important;
      background-clip: text !important;
    }
    
    /* Animation color updates */
    .animate-pulse-glow {
      animation: pulseGlow 1.5s ease-in-out infinite;
    }
    
    @keyframes pulseGlow {
      0%, 100% { box-shadow: 0 0 20px ${theme.cssVars.glowColor} !important; }
      50% { box-shadow: 0 0 30px ${theme.cssVars.glowColor.replace('0.15', '0.6')} !important; }
    }
  `;
};

/**
 * Apply theme to document
 */
export const applyTheme = (theme: ThemeConfig = ACTIVE_THEME): void => {
  const style = document.createElement('style');
  style.id = 'dynamic-theme';
  style.textContent = generateThemeCSS(theme);
  
  // Remove existing dynamic theme
  const existing = document.getElementById('dynamic-theme');
  if (existing) {
    existing.remove();
  }
  
  document.head.appendChild(style);
};

/**
 * Switch theme by name
 */
export const switchTheme = (themeName: string): boolean => {
  const theme = getThemeByName(themeName);
  if (theme) {
    applyTheme(theme);
    return true;
  }
  return false;
};

export default ACTIVE_THEME;